import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Note, Folder, Tag, ChecklistItem } from '../types';
import { nanoid } from 'nanoid';
import { hybridDbService } from '../services/hybridDatabase';

interface NoteStore {
  notes: Note[];
  folders: Folder[];
  tags: Tag[];
  currentNote: Note | null;
  searchQuery: string;
  selectedFolder: string | null;
  selectedTags: string[];
  
  // Actions
  createNote: (type?: 'text' | 'checklist' | 'password') => Promise<Note>;
  updateNote: (id: string, updates: Partial<Note>) => Promise<void>;
  deleteNote: (id: string) => Promise<void>;
  setCurrentNote: (note: Note | null) => void;
  
  // Checklist actions
  addChecklistItem: (noteId: string, text: string) => Promise<void>;
  updateChecklistItem: (noteId: string, itemId: string, updates: Partial<ChecklistItem>) => Promise<void>;
  deleteChecklistItem: (noteId: string, itemId: string) => Promise<void>;
  
  // Folder actions
  createFolder: (name: string, color: string) => Promise<Folder>;
  updateFolder: (id: string, updates: Partial<Folder>) => Promise<void>;
  deleteFolder: (id: string) => Promise<void>;
  
  // Tag actions
  addTag: (noteId: string, tagName: string) => Promise<void>;
  removeTag: (noteId: string, tagName: string) => Promise<void>;
  
  // Search and filter
  setSearchQuery: (query: string) => void;
  setSelectedFolder: (folderId: string | null) => void;
  setSelectedTags: (tags: string[]) => void;
  
  // Data loading
  loadNotes: () => Promise<void>;
  loadFolders: () => Promise<void>;
  loadTags: () => Promise<void>;
}

export const useNoteStore = create<NoteStore>()(
  persist(
    (set, get) => ({
      notes: [],
      folders: [],
      tags: [],
      currentNote: null,
      searchQuery: '',
      selectedFolder: null,
      selectedTags: [],

      createNote: async (type: 'text' | 'checklist' | 'password' = 'text') => {
        const newNote: Note = {
          id: nanoid(),
          title: '', // Start with empty title so user can type immediately
          content: '',
          type,
          tags: type === 'password' ? ['password'] : [],
          folderId: get().selectedFolder || undefined,
          createdAt: new Date(),
          updatedAt: new Date(),
          isPinned: false,
          isLocked: type === 'password', // Password notes are locked by default
          checklistItems: type === 'checklist' ? [] : undefined,
          passwordData: type === 'password' ? {
            website: '',
            username: '',
            password: '',
            notes: '',
          } : undefined,
        };

        await hybridDbService.addNote(newNote);
        set(state => ({ 
          notes: [newNote, ...state.notes],
          currentNote: newNote 
        }));
        
        return newNote;
      },

      updateNote: async (id, updates) => {
        const updatedNote = { ...updates, updatedAt: new Date() };
        
        // Skip history for simple folder moves to prevent hanging
        const skipHistory = updates.folderId !== undefined && Object.keys(updates).length === 2; // folderId + updatedAt
        
        await hybridDbService.updateNote(id, updatedNote, skipHistory);
        
        set(state => ({
          notes: state.notes.map(note => 
            note.id === id ? { ...note, ...updatedNote } : note
          ),
          currentNote: state.currentNote?.id === id 
            ? { ...state.currentNote, ...updatedNote } 
            : state.currentNote
        }));
      },

      deleteNote: async (id) => {
        await hybridDbService.deleteNote(id);
        set(state => ({
          notes: state.notes.filter(note => note.id !== id),
          currentNote: state.currentNote?.id === id ? null : state.currentNote
        }));
      },

      setCurrentNote: (note) => set({ currentNote: note }),

      addChecklistItem: async (noteId, text) => {
        const newItem: ChecklistItem = {
          id: nanoid(),
          text,
          completed: false,
          createdAt: new Date(),
        };

        const note = get().notes.find(n => n.id === noteId);
        if (!note) return;

        const updatedItems = [...(note.checklistItems || []), newItem];
        await get().updateNote(noteId, { checklistItems: updatedItems });
      },

      updateChecklistItem: async (noteId, itemId, updates) => {
        const note = get().notes.find(n => n.id === noteId);
        if (!note || !note.checklistItems) return;

        const updatedItems = note.checklistItems.map(item =>
          item.id === itemId ? { ...item, ...updates } : item
        );
        
        await get().updateNote(noteId, { checklistItems: updatedItems });
      },

      deleteChecklistItem: async (noteId, itemId) => {
        const note = get().notes.find(n => n.id === noteId);
        if (!note || !note.checklistItems) return;

        const updatedItems = note.checklistItems.filter(item => item.id !== itemId);
        await get().updateNote(noteId, { checklistItems: updatedItems });
      },

      createFolder: async (name, color) => {
        const newFolder: Folder = {
          id: nanoid(),
          name,
          color,
          createdAt: new Date(),
          updatedAt: new Date(),
        };

        await hybridDbService.addFolder(newFolder);
        set(state => ({ folders: [...state.folders, newFolder] }));
        return newFolder;
      },

      updateFolder: async (id, updates) => {
        const updatedFolder = { ...updates, updatedAt: new Date() };
        await hybridDbService.updateFolder(id, updatedFolder);
        
        set(state => ({
          folders: state.folders.map(folder =>
            folder.id === id ? { ...folder, ...updatedFolder } : folder
          )
        }));
      },

      deleteFolder: async (id) => {
        await hybridDbService.deleteFolder(id);
        set(state => ({
          folders: state.folders.filter(folder => folder.id !== id),
          selectedFolder: state.selectedFolder === id ? null : state.selectedFolder
        }));
      },

      addTag: async (noteId, tagName) => {
        const note = get().notes.find(n => n.id === noteId);
        if (!note || note.tags.includes(tagName)) return;

        const updatedTags = [...note.tags, tagName];
        await get().updateNote(noteId, { tags: updatedTags });
        await get().loadTags(); // Refresh tag counts
      },

      removeTag: async (noteId, tagName) => {
        const note = get().notes.find(n => n.id === noteId);
        if (!note) return;

        const updatedTags = note.tags.filter(tag => tag !== tagName);
        await get().updateNote(noteId, { tags: updatedTags });
        await get().loadTags(); // Refresh tag counts
      },

      setSearchQuery: (query) => set({ searchQuery: query }),
      setSelectedFolder: (folderId) => set({ selectedFolder: folderId }),
      setSelectedTags: (tags) => set({ selectedTags: tags }),

      loadNotes: async () => {
        const notes = await hybridDbService.getAllNotes();
        set({ notes });
      },

      loadFolders: async () => {
        const folders = await hybridDbService.getAllFolders();
        set({ folders });
      },

      loadTags: async () => {
        const notes = get().notes;
        const tagCounts = new Map<string, number>();
        
        notes.forEach(note => {
          note.tags.forEach(tag => {
            tagCounts.set(tag, (tagCounts.get(tag) || 0) + 1);
          });
        });

        const tags: Tag[] = Array.from(tagCounts.entries()).map(([name, count]) => ({
          id: nanoid(),
          name,
          color: `hsl(${Math.random() * 360}, 70%, 80%)`,
          count,
        }));

        set({ tags });
      },
    }),
    {
      name: 'notepad-store',
      partialize: (state) => ({
        selectedFolder: state.selectedFolder,
        selectedTags: state.selectedTags,
      }),
    }
  )
);